import { ResultSet } from "@cubejs-client/core";
import { AppThunk } from "appThunk";
import { DateTime } from "luxon";
import { cubeLoad } from "modules/helpers/cube/cubeSlice";
import { logError } from "modules/helpers/logger/loggerSlice";

export enum MonthPeriodLabel {
    Month = "month",
    Period = "period"
}

export class CustomDate {
    public readonly date: DateTime;
    public readonly periodNumber: number;
    public readonly quarterNumber: number;
    public readonly year: number;
    public readonly financialYearNumber: number;
    public readonly period: string;
    public readonly quarter: string;
    public readonly quarterLabel: string;
    public readonly dateLabel: string;
    public readonly financialYearLabel: string;

    constructor(
        date: DateTime,
        periodNumber: number,
        quarterNumber: number,
        year: number,
    ) {
        this.date = date;
        this.periodNumber = periodNumber;
        this.quarterNumber = quarterNumber;
        this.year = year;
        this.financialYearNumber = Number(String(year).slice(2));
        this.period = `P${periodNumber} ${year}`;
        this.quarter = `Q${quarterNumber} ${year}`;
        this.quarterLabel = `Q${quarterNumber} FY${String(year).slice(2)}`;
        this.dateLabel = `P${periodNumber} FY${String(year).slice(2)}`;
        this.financialYearLabel = `FY${String(year).slice(2)}`;
    }
}

export const loadHasCustomCalendar = (): AppThunk<Promise<boolean>> => async (dispatch) => {
    try {
        const query = {
            dimensions: [
                "ClientRegistration.HasCustomCalendar"
            ]
        };
        const resultSet = await dispatch(cubeLoad(query)) as unknown as ResultSet;
        const rawData = resultSet.rawData();
        return Boolean(rawData[0]["ClientRegistration.HasCustomCalendar"]);
    } catch (error) {
        dispatch(logError("Error loading HasCustomCalendar.", error));
        throw error;
    }
};

export const loadNumberOfPeriods = (useCustomCalendar: boolean, referenceDate: DateTime): AppThunk<Promise<number>> => async (dispatch) => {
    try {
        if (!useCustomCalendar) {
            return 12;
        }

        const financialYearQuery = {
            filters: [{
                "member": "D_Date.Date",
                "operator": "equals",
                "values": [referenceDate]
            }],
            dimensions: [
                "D_Date.FY"
            ]
        };
        const financialYearResultSet = await dispatch(cubeLoad(financialYearQuery)) as unknown as ResultSet;
        const financialYearNumber = financialYearResultSet.rawData()[0]["D_Date.FY"];

        const countQuery = {
            filters: [{
                "member": "D_Date.FY",
                "operator": "equals",
                "values": [String(financialYearNumber)]
            }],
            measures: [
                "D_Date.CountDistinctPeriods",
            ]
        };
        const countResultSet = await dispatch(cubeLoad(countQuery)) as unknown as ResultSet;
        return Number(countResultSet.rawData()[0]["D_Date.CountDistinctPeriods"]);
    } catch (error) {
        dispatch(logError("Error loading number of periods.", error));
        throw error;
    }
};

export const getDateDimensionNames = (useCustomCalendar: boolean) => ({
    monthPeriodNumber: useCustomCalendar ? "PeriodNo" : "MonthNo",
    monthPeriodStartDate: useCustomCalendar ? "PeriodStartDate" : "MonthStartDate",
    monthPeriodEndDate: useCustomCalendar ? "PeriodEndDate" : "MonthEndDate",
    maxMonthPeriodStartDate: useCustomCalendar ? "MaxPeriodStartDate" : "MaxMonthStartDate",
    maxMonthPeriodEndDate: useCustomCalendar ? "MaxPeriodEndDate" : "MaxMonthEndDate",
    yearNumber: useCustomCalendar ? "FY" : "Year"
});
